import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Real coordinates for Texas Hill Country cities
const cityData = [
  { id: 'bulverde', name: 'Bulverde', lat: 29.7436, lng: -98.4531, county: 'Comal County', isHQ: true },
  { id: 'new-braunfels', name: 'New Braunfels', lat: 29.7030, lng: -98.1245, county: 'Comal County' },
  { id: 'boerne', name: 'Boerne', lat: 29.7947, lng: -98.7320, county: 'Kendall County' },
  { id: 'canyon-lake', name: 'Canyon Lake', lat: 29.8752, lng: -98.2625, county: 'Comal County' },
  { id: 'spring-branch', name: 'Spring Branch', lat: 29.8669, lng: -98.4097, county: 'Comal County' },
  { id: 'blanco', name: 'Blanco', lat: 30.0977, lng: -98.4214, county: 'Blanco County' },
  { id: 'fredericksburg', name: 'Fredericksburg', lat: 30.2752, lng: -98.8720, county: 'Gillespie County' },
  { id: 'wimberley', name: 'Wimberley', lat: 29.9974, lng: -98.0986, county: 'Hays County' },
  { id: 'dripping-springs', name: 'Dripping Springs', lat: 30.1902, lng: -98.0867, county: 'Hays County' },
  { id: 'driftwood', name: 'Driftwood', lat: 30.1113, lng: -98.0225, county: 'Hays County' },
  { id: 'johnson-city', name: 'Johnson City', lat: 30.2766, lng: -98.4120, county: 'Blanco County' },
  { id: 'stonewall', name: 'Stonewall', lat: 30.2385, lng: -98.6628, county: 'Gillespie County' },
  { id: 'kendalia', name: 'Kendalia', lat: 29.9591, lng: -98.5231, county: 'Kendall County' },
  { id: 'marble-falls', name: 'Marble Falls', lat: 30.5782, lng: -98.2753, county: 'Burnet County' },
  { id: 'lakeway', name: 'Lakeway', lat: 30.3638, lng: -97.9795, county: 'Travis County' },
  { id: 'spicewood', name: 'Spicewood', lat: 30.4749, lng: -98.1578, county: 'Burnet County' },
];

// Custom marker icons
const createIcon = (isHQ) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${isHQ ? '36px' : '28px'};
        height: ${isHQ ? '36px' : '28px'};
        background: ${isHQ ? '#22d3ee' : '#ffffff'};
        border: 3px solid ${isHQ ? '#ffffff' : '#22d3ee'};
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        ${isHQ ? 'animation: pulse 2s infinite;' : ''}
      ">
        <svg width="${isHQ ? '16' : '12'}" height="${isHQ ? '16' : '12'}" viewBox="0 0 24 24" fill="none" stroke="${isHQ ? '#000' : '#22d3ee'}" stroke-width="2.5">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      </div>
    `,
    iconSize: [isHQ ? 36 : 28, isHQ ? 36 : 28],
    iconAnchor: [isHQ ? 18 : 14, isHQ ? 18 : 14],
    popupAnchor: [0, isHQ ? -20 : -16],
  });
};

// Map bounds fitter component
function FitBounds({ cities }) {
  const map = useMap();
  
  useEffect(() => {
    const bounds = L.latLngBounds(cities.map(city => [city.lat, city.lng]));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [map, cities]);
  
  return null;
}

export default function ServiceAreaMapGoogle() {
  const [selectedCity, setSelectedCity] = useState(null);
  const center = [30.05, -98.4]; // Center of Hill Country

  return (
    <div className="bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="text-center py-6 px-4 bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900">
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Our Service Area
        </h3>
        <p className="text-zinc-400">
          Proudly serving <span className="text-cyan-400 font-semibold">16 communities</span> across the Texas Hill Country
        </p>
      </div>

      {/* Map Container */}
      <div className="relative">
        <style>{`
          .custom-marker {
            background: transparent !important;
            border: none !important;
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          .leaflet-popup-content-wrapper {
            background: #18181b;
            color: white;
            border-radius: 12px;
            border: 1px solid rgba(34, 211, 238, 0.3);
          }
          .leaflet-popup-tip {
            background: #18181b;
          }
          .leaflet-popup-content {
            margin: 0;
          }
          .leaflet-container {
            font-family: inherit;
          }
        `}</style>
        
        <MapContainer
          center={center}
          zoom={9}
          style={{ height: '450px', width: '100%' }}
          scrollWheelZoom={false}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          <FitBounds cities={cityData} />
          
          {cityData.map((city) => (
            <Marker
              key={city.id}
              position={[city.lat, city.lng]}
              icon={createIcon(city.isHQ)}
              eventHandlers={{
                click: () => setSelectedCity(city.id),
              }}
            >
              <Popup>
                <div className="p-3 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className={`w-4 h-4 ${city.isHQ ? 'text-cyan-400' : 'text-white'}`} />
                    <span className={`font-bold ${city.isHQ ? 'text-cyan-400' : 'text-white'}`}>
                      {city.name}, TX
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm mb-3">{city.county}</p>
                  {city.isHQ && (
                    <div className="bg-cyan-500/20 rounded-lg px-3 py-2 mb-3">
                      <p className="text-cyan-400 text-xs font-medium">📍 Headquarters Location</p>
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <Link 
                      to={`/shower-glass-${city.id}`}
                      className="flex items-center justify-between text-sm text-zinc-300 hover:text-cyan-400 transition-colors"
                    >
                      <span>View Services</span>
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* City List */}
      <div className="p-6 bg-zinc-900/50">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-4xl mx-auto">
          {cityData.map((city) => (
            <div
              key={city.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                city.isHQ 
                  ? 'bg-cyan-500/20 border border-cyan-500/50' 
                  : 'bg-zinc-800/50 hover:bg-zinc-800 border border-transparent hover:border-zinc-700'
              }`}
            >
              <MapPin className={`w-3 h-3 flex-shrink-0 ${city.isHQ ? 'text-cyan-400' : 'text-zinc-500'}`} />
              <span className={`text-sm ${city.isHQ ? 'text-cyan-400 font-medium' : 'text-zinc-300'}`}>
                {city.name}
              </span>
            </div>
          ))}
        </div>
        
        {/* CTA */}
        <div className="mt-6 text-center">
          <p className="text-zinc-500 text-sm mb-3">
            Don't see your area? We may still be able to help!
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-6 py-2.5 rounded-full transition-colors"
          >
            <Phone className="w-4 h-4" />
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}
